import React from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { eventLabel } from '../config/sports'
import { MARKET_TYPE_MAPPING, SELECTION_SHORT_NAMES } from '../config/markets'
import SubjectImage from '../components/SubjectImage'
import { TourMarker } from '../components/ToolTour'
import '../styles/app-mobile.css'
import './EVNew.css'

// One +EV opportunity as a mobile card. Used by EVNewMobile for every row and
// by the EV tour (on desktop too), so the tour always shows the real card.
// Everything that depends on the user's settings (stake, EV in % or $, odds
// format) arrives already formatted; the card only lays it out.

const formatMarketType = (mt) => {
  if (MARKET_TYPE_MAPPING[mt]) return MARKET_TYPE_MAPPING[mt]
  return (mt || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Match EV8's formatSelection — short labels for known market types where applicable
const formatSelection = (selection, marketType, playerName = '') => {
  if (!selection) return ''
  if (SELECTION_SHORT_NAMES[marketType]) {
    const parts = selection.split(' ')
    for (let i = 0; i < parts.length; i++) {
      const word = parts[i]?.toLowerCase()
      if (word === 'over' || word === 'under') {
        const next = parts[i + 1]
        if (next && next.replace('.', '').replace('-', '').match(/^\d+$/)) {
          if (playerName) {
            return `${playerName} ${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
          }
          return `${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
        }
      }
    }
  }
  return selection
}

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })

const noop = () => {}

/**
 * markers: tour only, { market, selection, books, odds, ev, stake, track } -> number
 * children: rendered at the bottom of the card (the expanded odds, error tip)
 */
export default function EVCard({
  opp, className = '', style, blurPick = false,
  evText, evClass, oddsText, stakeText,
  inMulti = false, legIdx = -1, isExpanded = false, tracked = false, showButtons = true,
  onToggleEv = noop, onOpenBook = noop, onMulti = noop, onExpand = noop, onTrack = noop, onShare = noop, onHide = noop,
  markers = {}, children,
}) {
  const mark = (part, style) => markers[part] != null && <TourMarker n={markers[part]} style={style} />
  const anchor = (part) => (markers[part] != null ? { position: 'relative' } : null)
  const blurText = blurPick ? { filter: 'blur(5px)', userSelect: 'none' } : {}

  return (
    <div className={`bcm-card${inMulti ? ' bcm-card-in-multi' : ''}${className ? ` ${className}` : ''}`} style={style}>

      {/* Market type strip */}
      {/* The strip clips its own overflow, so a marker sits beside the text inside it */}
      <div className="bcm-card-type-strip">
        {markers.market != null ? (
          <span style={{ position: 'relative' }}>
            {formatMarketType(opp.market_type)}
            {mark('market', { top: '-4px', right: '-28px' })}
          </span>
        ) : formatMarketType(opp.market_type)}
      </div>

      {/* EV corner ribbon — tap to toggle % ↔ $ */}
      <div
        className={`bcm-ev-corner ${evClass}`}
        onClick={(e) => { e.stopPropagation(); onToggleEv() }}
        style={{ cursor: 'pointer' }}
      >
        {evText}
        {mark('ev', { top: '-8px', right: '-6px' })}
      </div>

      {/* Card top: (match / time) → selection pill, subject image RIGHT.
          Prop EV leads with the image because its card is ABOUT a
          player. An EV card leads with the fixture, so an image on the
          left pushes every line of text in and competes with the type
          strip above it; on the right it fills the space the pill
          leaves and lands on the same edge as the footer's Stake.
          The 30px right padding that used to clear the EV ribbon is
          gone — the image is centred in the row instead, which puts it
          below the ribbon's overhang without insetting anything. */}
      <div className="bcm-card-top">
        <div className="bcm-card-name-block">
          <div className="bcm-card-match">{opp.match}</div>
          <div className="bcm-card-meta-row">
            <span className="bc-sport-badge">{eventLabel(opp)}</span>
            <span className="bcm-card-time">{formatDateTime(opp.commence_time)}</span>
          </div>
          {/* The pill clips its own overflow, so a marker sits on a wrapper around it */}
          {markers.selection != null ? (
            <span style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
              <span className="bcm-card-selection-pill" style={blurText}>
                {formatSelection(opp.selection, opp.market_type, opp.player_name)}
              </span>
              {mark('selection', { top: '-1px' })}
            </span>
          ) : (
            <span className="bcm-card-selection-pill" style={blurText}>
              {formatSelection(opp.selection, opp.market_type, opp.player_name)}
            </span>
          )}
        </div>
        <div className="bcm-ev-subject">
          <SubjectImage opp={opp} />
        </div>
      </div>

      {/* Card footer: logos + odds | stake */}
      <div className="bcm-card-footer">
        <div className="bcm-card-footer-left">
          <div className="bcm-footer-books" style={anchor('books')}>
            {opp.bookmakers.slice(0, 4).map((b, i) => (
              BOOKMAKER_LOGOS[b.name] ? (
                <img
                  key={b.name}
                  src={BOOKMAKER_LOGOS[b.name]}
                  alt={b.name}
                  className="bcm-footer-logo"
                  style={{ zIndex: opp.bookmakers.length - i }}
                  onClick={() => onOpenBook(b)}
                />
              ) : null
            ))}
            {opp.bookmakers.length > 4 && (
              <span className="bcm-footer-books-more">+{opp.bookmakers.length - 4}</span>
            )}
            {mark('books')}
          </div>
          <span
            className="bcm-footer-odds"
            style={{ ...blurText, ...anchor('odds') }}
          >
            {oddsText}
            {mark('odds', { top: '-4px', right: '-26px' })}
          </span>
        </div>

        <div className="bcm-card-footer-right">
          <div className="bcm-footer-stake-block" style={anchor('stake')}>
            <span className="bcm-footer-stake-label">Stake</span>
            <span className="bcm-footer-stake-val">{stakeText}</span>
            {mark('stake')}
          </div>
        </div>
      </div>

      {/* Button strip */}
      {showButtons && (
        <div className="bcm-btn-strip">
          <button
            className={`bcm-strip-multi${inMulti ? ' in-multi' : ''}`}
            onClick={onMulti}
          >
            {inMulti && <span>{legIdx + 1}</span>}
          </button>
          <div className="bcm-strip-right">
            <button
              className={`bcm-strip-btn${isExpanded ? ' active' : ''}`}
              onClick={onExpand}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
            <button
              className={`bcm-strip-btn${tracked ? ' tracked' : ''}`}
              onClick={onTrack}
              style={anchor('track')}
            >
              {tracked
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              }
              {mark('track')}
            </button>
            <button className="bcm-strip-btn" onClick={onShare}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            <button className="bcm-strip-btn hide-btn" onClick={onHide}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
